import { countObjectsByType } from '@utils/library/mapStrapiLibraries';
import { resolveStrapiUrl } from '@utils/library/resolveStrapiUrl';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';

import {
  KEEPSIMPLE_URL,
  LIBRARY_SHELVES_REFETCH_EVENT,
  MAX_TAGS_PER_LIBRARY,
  TAG_LIMIT_MESSAGE,
} from '@constants/library/common';

import { ITagAttributes } from '@local-types/library/tag';

import { useAnimatedList } from '@hooks/library/useAnimatedList';
import useLibraryEditing from '@hooks/library/useLibraryEditing';
import { useLockBodyScroll } from '@hooks/library/useLockBodyScroll';

import { libraryPath } from '@lib/library/libraryPath';
import { richTextLength } from '@lib/library/richText';

import { createTag, CreateTagRequest } from '@api/library/tag/createTag';
import { deleteTag } from '@api/library/tag/deleteTag';
import { updateTag, UpdateTagRequest } from '@api/library/tag/updateTag';

import avatarImage from '@icons/library/images/avatar.png';
import {
  CloseIcon,
  EditIcon,
  LinkIcon,
  PanelIcon,
  PlusIcon,
} from '@icons/library/svg';

import { useAuth } from '@components/Context/library/AuthContext';
import { useDashboard } from '@components/Context/library/DashboardContext';
import { useGlobalState } from '@components/Context/library/GlobalStateContext';
import { Avatar } from '@components/library/atoms/Avatar';
import CopyButtonLabel from '@components/library/atoms/CopyButtonLabel';
import ExpandableText from '@components/library/atoms/ExpandableText';
import { InkLine } from '@components/library/atoms/InkLine';
import { Text, TypographyVariant } from '@components/library/atoms/Text';
import { Toggle } from '@components/library/atoms/Toggle';
import { Tooltip } from '@components/library/atoms/Tooltip';
import {
  Button,
  ButtonSize,
  ButtonType,
} from '@components/library/molecules/Button';
import {
  CreateTagFormData,
  CreateTagModal,
} from '@components/library/molecules/CreateTagModal';
import { Object, ObjectType } from '@components/library/molecules/Object';
import { Tag } from '@components/library/molecules/Tag';
import { EditLibraryModal } from '@components/library/organisms/EditLibraryModal';

import styles from './Sidebar.module.scss';

// motion-passport: exempt. The drawer host added below is layout only
// (display: contents on desktop, a fixed clipping layer on the drawer
// breakpoints) and animates nothing. The panel-slide transition and its
// prefers-reduced-motion branch live in Sidebar.module.scss.

// aboutMe / aboutLibrary hold rich text: the owner's line breaks and marks are
// part of what they wrote, so the panel renders the stored markup instead of
// flattening it. Emptiness is judged on the text alone, never on the tags.
const hasText = (value?: string | null) => richTextLength(value) > 0;

const tagKey = <T extends { name: string }>(tag: T) => tag.name;

export function Sidebar() {
  const router = useRouter();

  const { accountData } = useAuth();
  const { libraryTags, tags, refreshLibraryTags, activeTagId, setActiveTagId } =
    useDashboard();
  const {
    isSidebarOpen,
    isSidebarCollapsed,
    isGuestMode,
    toggleSidebar,
    toggleGuestMode,
    currentShelves,
    currentOwner,
    currentLibrary,
    isOwner,
  } = useGlobalState();

  // The library being viewed is always the `[username]` route segment — read it
  // from the router params, not the URL tail. On nested routes
  // (`/library/[username]/[slug]`, `/library/[username]/share/[token]`) the tail
  // is the object slug or share token, not the library.
  const usernameParam = router.query.username;
  const currentLibraryId =
    (Array.isArray(usernameParam) ? usernameParam[0] : usernameParam) ?? '';

  // A copied library link is always the public version, even when the owner is
  // editing it on the private review host.
  const linkUsername = /^\d+$/.test(currentLibraryId)
    ? currentOwner?.username
    : currentLibraryId;
  const shareUrl = `${KEEPSIMPLE_URL}${libraryPath(linkUsername)}`;

  const [isOpenTagModal, setIsOpenTagModal] = useState<
    null | 'create' | 'edit'
  >(null);
  const [selectedTag, setSelectedTag] = useState<ITagAttributes | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isEditLibraryOpen, setIsEditLibraryOpen] = useState(false);

  useEffect(() => {
    if (!isCopied) return;
    const resetCopied = window.setTimeout(() => setIsCopied(false), 2000);
    return () => window.clearTimeout(resetCopied);
  }, [isCopied]);

  // Object totals always come from the live shelves of the library on screen
  // (`currentShelves`, published by LibraryTemplate) — never from the viewer's
  // own library list, which would show the wrong counts on someone else's page.
  // Those shelves are already the ones the viewer may see (private ones are
  // filtered out for visitors and in guest mode), so count all of them.
  const { bookCount, videoCount, songCount } = useMemo(
    () => countObjectsByType(currentShelves, { includePrivate: true }),
    [currentShelves],
  );

  // The whole panel is decided by one question: is this my library, and am I
  // logged in? Yes → editable, showing my account identity. No (someone else's
  // library, or logged out) → read-only, showing the viewed library's public
  // data. Guest mode lets an owner preview that read-only view.
  //
  // The answer is not computed here. LibraryTemplate decides ownership once
  // and publishes it, so this panel and the shelves can never disagree about
  // who is looking (they used to, on a numeric `/library/123` address).
  const isMyLibrary = isOwner;
  // Phones are read-only: no Edit library, no tag editing (the same rule the
  // shelves follow through LibraryTemplate's canEditHere).
  const supportsEditing = useLibraryEditing();
  const canEdit = isMyLibrary && !isGuestMode && supportsEditing;

  // The drawer is an overlay on phones and tablets: while it is open the page
  // under it must not move under a swipe, same as every modal.
  useLockBodyScroll(isSidebarOpen);

  // An owner can edit their About panel before any library row exists: the
  // row is created lazily on first save, and any signed-in owner of this
  // address may create one (since 2026-09-12), so `canEdit` is the whole gate.

  // The public owner profile supplies the same identity and photo to every visitor.
  const slugName = /^\d+$/.test(currentLibraryId) ? '' : currentLibraryId;
  const authorName = canEdit
    ? accountData?.username || currentOwner?.username || 'Anonymous'
    : currentOwner?.username || slugName || 'Anonymous';
  const authorAvatarUrl =
    resolveStrapiUrl(currentOwner?.avatar) ??
    resolveStrapiUrl(currentOwner?.picture) ??
    (canEdit ? accountData?.picture : undefined);
  const aboutAuthorText = currentOwner?.aboutMe ?? '';
  const aboutLibraryText =
    currentLibrary?.attributes.libraryDetails?.aboutLibrary ?? '';

  // Everything the viewer can open right now. A tag's row is drawn from it,
  // so an owner previewing as a guest sees the same tags a visitor does.
  const visibleObjectIds = useMemo(() => {
    const ids = new Set<number>();
    for (const shelf of currentShelves) {
      for (const obj of shelf.attributes.objects?.data ?? []) ids.add(obj.id);
    }
    return ids;
  }, [currentShelves]);

  // The library's own tags, each with the number of books it labels here. The
  // owner keeps their whole palette on screen, empty tags included, because it
  // is the vocabulary they write with; a visitor is shown only the tags that
  // lead somewhere. A tag labelling nothing is not a filter, so it does not
  // answer a click.
  const displayedTags = useMemo(
    () =>
      libraryTags
        .map(tag => ({
          ...tag,
          count: tag.objects.filter(id => visibleObjectIds.has(id)).length,
        }))
        .filter(tag => canEdit || tag.count > 0),
    [libraryTags, visibleObjectIds, canEdit],
  );

  const atTagLimit = libraryTags.length >= MAX_TAGS_PER_LIBRARY;

  // A created tag rises into the row, a deleted one fades where it stood and
  // the rest slide over, instead of the whole row re-wrapping in one frame.
  const { ref: tagsRef, entries: tagEntries } = useAnimatedList(
    displayedTags,
    tagKey,
    { collapse: 'width' },
  );

  const [copyError, setCopyError] = useState<string | null>(null);
  const handleCopyUrl = async () => {
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
    } catch {
      // Clipboard API is unavailable on insecure origins / older browsers —
      // fall back to a throwaway textarea + execCommand, as the overview's
      // Share button does; failing that, say so instead of a silent click.
      try {
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!ok) throw new Error('execCommand failed');
        setIsCopied(true);
      } catch {
        setCopyError('Could not copy the library URL.');
      }
    }
  };

  // Tags live on shelf cards and in the search index as well as in this
  // panel, so a rename, recolour or delete reloads the library too.
  const refetchLibrary = () => {
    window.dispatchEvent(new CustomEvent(LIBRARY_SHELVES_REFETCH_EVENT));
  };

  const handleCreateTag = async (formData: CreateTagFormData) => {
    try {
      // Throw rather than return: the modal reads a quiet resolve as a saved
      // tag and shows the success card, so a silent bail claimed a tag that
      // was never created.
      if (!accountData?.id || !currentLibrary?.id) {
        throw new Error('No library to create this tag in');
      }

      const body: CreateTagRequest = {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        user: accountData?.id,
        library: currentLibrary.id,
      };

      await createTag(body);
      await refreshLibraryTags();
    } catch (error) {
      console.error('Failed to create or refresh tags:', error);
      throw error;
    }
  };

  const handleEditTag = async (formData: CreateTagFormData) => {
    try {
      if (!selectedTag || !accountData?.id) return;

      const body: UpdateTagRequest = {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        user: accountData?.id,
      };

      await updateTag(selectedTag.id, body);
      await refreshLibraryTags();
      refetchLibrary();
      setIsOpenTagModal(null);
      setSelectedTag(null);
    } catch (error) {
      console.error('Failed to edit tag:', error);
      throw error;
    }
  };

  const handleDeleteTag = async () => {
    if (!selectedTag || !accountData?.id) return;

    try {
      await deleteTag(selectedTag.id);
      await refreshLibraryTags();
      refetchLibrary();
      setIsOpenTagModal(null);
      setSelectedTag(null);
    } catch (error) {
      console.error('Failed to delete tag:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  return (
    <>
      {/* Mobile-only fallback opener. The toolbar carries the About button
          beside the library name, but the toolbar only exists once the library
          has shelves — an empty library would otherwise have no way in. The
          backdrop taps the drawer closed. Both are hidden on desktop, where the
          panel is a static sticky column.

          Gated on `currentLibrary` because "still loading" and "loaded and
          empty" both read as zero shelves: LibraryTemplate nulls the library
          for the duration of a load, so without this the tab floated over the
          loading spinner on every mobile page load. */}
      {!isSidebarOpen && currentLibrary && currentShelves.length === 0 && (
        <button
          type="button"
          className={styles.openTab}
          onClick={toggleSidebar}
          aria-label="Open library info panel"
        >
          <PanelIcon />
        </button>
      )}
      {isSidebarOpen && (
        <div
          className={styles.backdrop}
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}
      {/* Desktop: the column folds away on the toolbar's panel toggle and
          unfolds from it; the choice is per account and survives a refresh
          (GlobalState). The drawer states above are phone/tablet only, and
          the CSS scopes each to its own breakpoint.

          The host is `display: contents` on desktop, so the panel stays a flex
          item of the dashboard row; at the drawer breakpoints it becomes a
          fixed, paint-contained layer that clips the panel parked off the right
          edge. Without it Safari counts that parked box towards the page's
          scrollable width and every library page measures about two screens
          wide on a phone. */}
      <div className={styles.drawerHost}>
        <aside
          id="library-info-panel"
          data-library-mode-surface
          className={classNames(styles.sidebar, {
            [styles.open]: isSidebarOpen,
            [styles.collapsed]: isSidebarCollapsed,
          })}
        >
          <div className={styles.close}>
            <Button
              onClick={toggleSidebar}
              type={ButtonType.Text}
              size={ButtonSize.Default}
              ariaLabel="Close"
              Icon={<CloseIcon />}
            />
          </div>

          <div className={styles.main}>
            <div className={styles.about}>
              <div className={styles.header}>
                <Text className={styles.label}>About</Text>
                {canEdit && (
                  <Button
                    label="Edit"
                    onClick={() => setIsEditLibraryOpen(true)}
                    type={ButtonType.Secondary}
                    size={ButtonSize.Default}
                    ariaLabel="Edit library"
                    Icon={<EditIcon />}
                    className={styles.button}
                    labelClassName={styles.text}
                  />
                )}
              </div>

              <div className={styles.content}>
                <div>
                  {/* Plain text: the field is CKEditor markup server-side, and
                    printing it raw showed the tags. Empty gets a line of its
                    own, like Author and Tags do. A written description folds
                    so it cannot push Content, Author and Tags off the sheet. */}
                  {hasText(aboutLibraryText) ? (
                    <ExpandableText
                      value={aboutLibraryText}
                      title="About"
                      className={styles.label}
                      subject="library description"
                    />
                  ) : (
                    <Text
                      className={classNames(styles.label, styles.emptyTags)}
                    >
                      {canEdit
                        ? 'No description yet. Add one with Edit'
                        : 'No description yet'}
                    </Text>
                  )}
                </div>
                <InkLine seed={7} className={styles.innerRule} />

                {/* A kind the library does not hold is not written as a zero:
                  neither its icon nor its number stands here (Wolf,
                  2026-09-10). A library with no books at all keeps the
                  Content heading off the panel too. */}
                {(bookCount > 0 || videoCount > 0 || songCount > 0) && (
                  <div className={styles.totalObjects}>
                    <Text className={styles.subLabel}>Content</Text>
                    <div className={styles.objects}>
                      {bookCount > 0 && (
                        <Object
                          className={styles.count}
                          type={ObjectType.Book}
                          number={bookCount}
                          noBorder
                        />
                      )}
                      {videoCount > 0 && (
                        <Object
                          className={styles.count}
                          type={ObjectType.Video}
                          number={videoCount}
                          noBorder
                        />
                      )}
                      {songCount > 0 && (
                        <Object
                          className={styles.count}
                          type={ObjectType.Audio}
                          number={songCount}
                          noBorder
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
              <InkLine seed={1} className={styles.sectionRule} />
            </div>

            <div className={styles.about}>
              <div className={styles.header}>
                <Text className={styles.label}>Author</Text>
              </div>

              <div className={styles.content}>
                <div className={styles.avatar}>
                  <Avatar
                    url={authorAvatarUrl ?? avatarImage}
                    className={styles.avatarImage}
                    sizes="86px"
                  />
                  <Text
                    className={styles.name}
                    variant={TypographyVariant.TextBaseBold}
                  >
                    {authorName}
                  </Text>
                </div>
                <ExpandableText
                  value={aboutAuthorText}
                  title="Author"
                  className={styles.text}
                  subject="author biography"
                />
              </div>
              <InkLine seed={2} className={styles.sectionRule} />
            </div>

            <div className={styles.about}>
              <div className={styles.header}>
                <Text className={styles.label}>Tags</Text>
                {/* Both tag controls sit in the header: a chip inside the
                  wrapping tag flow drifted every time a tag was added. */}
                {canEdit && (
                  <span className={styles.headerActions}>
                    {displayedTags.length > 0 && (
                      <Button
                        label="Edit"
                        ariaLabel="Edit tags"
                        onClick={() => {
                          setIsOpenTagModal('edit');
                        }}
                        type={ButtonType.Secondary}
                        size={ButtonSize.Default}
                        Icon={<EditIcon />}
                        className={styles.button}
                        labelClassName={styles.text}
                      />
                    )}
                    <Tooltip
                      place="bottom"
                      tooltipContent={atTagLimit ? TAG_LIMIT_MESSAGE : ''}
                      wrapperClassName={classNames({
                        [styles.tooltipOff]: !atTagLimit,
                      })}
                    >
                      <Button
                        label="Create"
                        ariaLabel="Create tag"
                        onClick={() => setIsOpenTagModal('create')}
                        type={ButtonType.Secondary}
                        size={ButtonSize.Default}
                        Icon={<PlusIcon />}
                        className={styles.button}
                        labelClassName={styles.text}
                        disabled={atTagLimit}
                      />
                    </Tooltip>
                  </span>
                )}
              </div>
              <div className={styles.content}>
                <div
                  ref={tagsRef}
                  className={classNames(styles.tags, {
                    [styles.tagsEmpty]: displayedTags.length === 0,
                  })}
                >
                  {displayedTags.length === 0 && (
                    <Text className={styles.emptyTags}>No tags yet</Text>
                  )}
                  {tagEntries.map(({ item: tag, leaving }) => (
                    <span
                      key={tag.name}
                      className={classNames(styles.tagSlot, {
                        [styles.tagLeaving]: leaving,
                      })}
                      data-flip-id={tag.name}
                      data-flip-leaving={leaving ? 'true' : undefined}
                      aria-hidden={leaving || undefined}
                    >
                      {/* This row is the only place a tag is a control. On the
                        cards, in the dossier and in the object overview a tag
                        stays a label. */}
                      <Tag
                        label={tag.name}
                        color={tag.color}
                        active={activeTagId === tag.id}
                        description={tag.description}
                        // A tag on no book has no row to open, so it says what
                        // it is instead of sitting there as a dead control.
                        hint={tag.count === 0 ? 'Tag not used' : undefined}
                        onClick={
                          tag.count > 0 && !leaving
                            ? () =>
                                setActiveTagId(
                                  activeTagId === tag.id ? null : tag.id,
                                )
                            : undefined
                        }
                      />
                    </span>
                  ))}
                </div>
              </div>
              <InkLine seed={3} className={styles.sectionRule} />
            </div>

            <div className={styles.about}>
              <div className={styles.content}>
                <Button
                  onClick={handleCopyUrl}
                  type={ButtonType.Secondary}
                  size={ButtonSize.Wide}
                  label={
                    <CopyButtonLabel copied={isCopied} label="Library URL" />
                  }
                  ariaLabel={
                    isCopied ? 'Library URL copied' : 'Copy library URL'
                  }
                  Icon={<LinkIcon />}
                  className={styles.copyButton}
                />
                <Text
                  variant={TypographyVariant.TextSmall}
                  className={classNames(styles.copyStatus, {
                    [styles.copyStatusError]: !!copyError,
                  })}
                  aria-live="polite"
                >
                  {copyError ?? ''}
                </Text>
              </div>
            </div>
          </div>

          {isMyLibrary && supportsEditing && (
            <div
              className={classNames(styles.footer, {
                [styles.footerActive]: isGuestMode,
              })}
            >
              {/* One line: the name and the switch. The explanation was two
                lines of the panel's height spent on a control whose state the
                page itself shows the moment it is flipped. */}
              <Text className={styles.label}>Guest mode</Text>
              <Toggle
                checked={isGuestMode}
                onChange={toggleGuestMode}
                ariaLabel="Guest mode"
              />
            </div>
          )}
        </aside>
      </div>
      {isOpenTagModal && (
        <CreateTagModal
          isEdit={isOpenTagModal === 'edit'}
          activeTag={selectedTag || undefined}
          tags={tags}
          onDelete={handleDeleteTag}
          onClose={() => {
            setIsOpenTagModal(null);
            setSelectedTag(null);
          }}
          onTagSelect={setSelectedTag}
          onSubmit={
            isOpenTagModal === 'create' ? handleCreateTag : handleEditTag
          }
        />
      )}
      {isEditLibraryOpen && canEdit && (
        <EditLibraryModal
          library={currentLibrary}
          onClose={() => setIsEditLibraryOpen(false)}
          onSaved={libraryId => {
            setIsEditLibraryOpen(false);
            // Reload the viewed library so the panel (avatar/bio/name) reflects
            // the save; LibraryTemplate re-publishes currentLibrary/currentOwner.
            // Carry the resolved id so a just-bootstrapped library loads by a
            // direct GET instead of the restricted owner relation-filter.
            window.dispatchEvent(
              new CustomEvent(LIBRARY_SHELVES_REFETCH_EVENT, {
                detail: { libraryId },
              }),
            );
          }}
        />
      )}
    </>
  );
}
